import React, { useState } from 'react';
import { useTeacherData } from '../../context/TeacherDataContext';
import { Card, Button, OutstandingBanner, Skeleton, Modal, AnimatePresence } from '../shared/UI';
import Badge from '../shared/Badge';
import { schoolFeeSummary, formatCurrency } from '../../utils/feeCalculations';
import { resolveTimelineInfo } from '../../../shared/utils/timelineText';
import { formatEventDateRange } from '../../../shared/eventDate';

// An outstanding item only surfaces once it's actually urgent — starting one
// week before its due date, and staying visible indefinitely once it's past
// due. Due dates are free-typed text from the Information sheet and may be
// unparseable; treat those as "not due soon" rather than always/never showing.
function isDueSoonOrPastDue(dateStr) {
  if (!dateStr) return false;
  const due = new Date(dateStr);
  if (isNaN(due)) return false;
  const oneWeekBefore = new Date(due);
  oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);
  return new Date() >= oneWeekBefore;
}

// True if ANY of the given dates is due-soon-or-past-due. Each outstanding
// item below is tied to exactly one Timeline deadline (passed as a
// single-element array), but this stays list-shaped for a uniform call site
// with earliestDate() below.
function isAnyDueSoonOrPastDue(...dateStrs) {
  return dateStrs.some(isDueSoonOrPastDue);
}

// Earliest valid date among the given date strings — used as both the
// displayed due date and the sort key.
function earliestDate(...dateStrs) {
  const parsed = dateStrs
    .map((s) => (s ? new Date(s) : null))
    .filter((d) => d && !isNaN(d));
  return parsed.length ? parsed.reduce((a, b) => (a < b ? a : b)) : null;
}

const STATUS_VARIANT = { complete: 'success', 'in-progress': 'warning', 'fee-pending': 'warning', 'not-registered': 'danger', waitlisted: 'warning', withdrawn: 'neutral', upcoming: 'neutral' };
const STATUS_LABEL = { complete: 'Complete', 'in-progress': 'In progress', 'fee-pending': 'Fee Outstanding', 'not-registered': 'Not Registered', waitlisted: 'Waitlisted', withdrawn: 'Withdrawn', upcoming: 'Upcoming' };

// Same light-background/dark-text pairing as each Badge variant (see
// shared/Badge.js), so the timeline dot never disagrees with the badge text
// sitting right next to it.
const DOT_STYLE = {
  complete: { background: '#E1F5EE', color: '#0F6E56', icon: '✓' },
  'in-progress': { background: '#FAEEDA', color: '#854F0B', icon: '•' },
  'fee-pending': { background: '#FAEEDA', color: '#854F0B', icon: '!' },
  'not-registered': { background: '#FCEBEB', color: '#A32D2D', icon: '!' },
  waitlisted: { background: '#FAEEDA', color: '#854F0B', icon: '⏳' },
  withdrawn: { background: '#F0F0F0', color: '#666666', icon: '⊘' },
  upcoming: { background: '#F0F0F0', color: '#666666', icon: '•' },
};

// Remembers (for this browser session only — sessionStorage, nothing
// personal) that the teacher closed the "register for the event first"
// popup, so it shows once per login rather than on every Dashboard visit.
const NOT_REGISTERED_NOTICE_KEY = 'ossdf-not-registered-notice-seen';
function noticeSeen() {
  try { return sessionStorage.getItem(NOT_REGISTERED_NOTICE_KEY) === '1'; } catch { return false; }
}
function markNoticeSeen() {
  try { sessionStorage.setItem(NOT_REGISTERED_NOTICE_KEY, '1'); } catch { /* storage unavailable — popup just shows again */ }
}

export default function Dashboard({ navigateTo }) {
  const { school, event, timeline, dancers, entries, staff, feeConfig, invoiceSummary, locks, entryFeePayments, loading } = useTeacherData();
  const [noticeDismissed, setNoticeDismissed] = useState(noticeSeen);
  const closeNotice = () => { markNoticeSeen(); setNoticeDismissed(true); };

  if (loading.timeline || loading.event || loading.dancers || loading.entries || loading.feeConfig || loading.invoiceSummary || loading.entryFeePayments || !school) {
    return (
      <div style={{ padding: 20, margin: '0 auto' }}>
        <Skeleton width={280} height={26} style={{ marginBottom: 10 }} />
        <Skeleton width={180} height={14} style={{ marginBottom: 20 }} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ background: '#F5F5F5', borderRadius: 8, padding: 14 }}>
              <Skeleton width="60%" height={12} style={{ marginBottom: 8 }} />
              <Skeleton width="40%" height={22} />
            </div>
          ))}
        </div>

        <Card title="Timeline">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '10px 0' }}>
              <Skeleton width={28} height={28} radius="50%" />
              <div style={{ flex: 1 }}>
                <Skeleton width="35%" height={14} style={{ marginBottom: 6 }} />
                <Skeleton width="55%" height={12} />
              </div>
            </div>
          ))}
        </Card>
      </div>
    );
  }

  const missingConsent = (dancers || []).filter((d) => d.studentSectionComplete && !d.parentSectionComplete);
  // Entries with no music file attached — Freestyle entries have no music
  // requirement, so they're excluded (see level === 'Freestyle', the same
  // signal entryRateFor uses elsewhere to detect a Freestyle entry).
  const missingMusic = (entries || []).filter((e) => e.level !== 'Freestyle' && !e.music);
  const summary = schoolFeeSummary(dancers, entries, feeConfig, invoiceSummary.schoolRegistrationFee, 0, entryFeePayments);
  const participationRemainingCount = summary.totalDancers - summary.participationPaidCount;

  // The event tab in the Information sheet contains a single row with the event name and date range, which is surfaced
  // in the timeline as a read-only row at the top. The event name is also used in the dashboard header and the season label.
  const currEvent = event?.[0];
  const eventYear = currEvent?.startDate?.match(/\d{4}/)?.[0];
  const eventDateRange = formatEventDateRange(currEvent?.startDate, currEvent?.endDate);

  // No fees are actually owed until the school has submitted event
  // registration — see the "Event Registration" row's status, driven by
  // getSchoolRegistrationStatus on the backend.
  const eventRegRow = (timeline || []).find((step) => step.title === 'Event Registration');
  const isEventRegistered = !eventRegRow || eventRegRow.status !== 'not-registered';

  const formatDate = (date) => date && date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  // Adds a "View invoice" action to the Timeline row for whichever fee
  // category still has money owing — same active-or-not conditions as the
  // outstanding-items banner below, so the two can never disagree about
  // what's actually due. Keyed by the row's exact title so it only touches
  // these 3 fee rows, leaving every other row's own action (e.g. "Manage
  // dancers", "Entry library") alone. The Event Registration row already
  // gets its own dynamic action from the backend ("Register for this year"
  // while not yet registered) — this only adds one once that's done and the
  // fee itself is still outstanding.
  const feeInvoiceActions = {
    'Event Registration': isEventRegistered && summary.schoolRegistrationRemaining > 0,
    'Participation Fee': isEventRegistered && summary.participationRemaining > 0,
    'Dance Entry Payment': isEventRegistered && summary.entryFeeRemaining > 0,
  };

  // A withdrawn school already paid its (non-refundable) registration fee
  // and isn't attending — nothing further is genuinely "outstanding" for
  // them, so skip the whole banner rather than nagging about fees/consent/
  // music for participants and entries that no longer count anyway.
  const isWithdrawn = !!locks?.withdrawn;

  // Registering for the event now has to come first (see
  // getSchoolBlockStatus on the backend). Some schools added people/entries
  // before that rule existed, so they also get a one-time popup.
  const notRegistered = !!locks?.notRegistered;
  const hasExistingRegistrations = (dancers || []).length > 0 || (entries || []).length > 0 || (staff || []).length > 0;

  // Each candidate is tied to exactly one Timeline deadline and only
  // surfaces once that deadline is due-soon-or-past-due; the resulting list
  // is sorted so the nearest deadline is first.
  const outstandingItems = isWithdrawn ? [] : [
    {
      // School registration and the school fee itself are both due by the
      // school registration deadline.
      active: isEventRegistered && summary.schoolRegistrationRemaining > 0,
      dates: [invoiceSummary.schoolRegistrationDueDate],
      label: `School registration fee outstanding — ${formatCurrency(summary.schoolRegistrationRemaining)}`,
    },
    {
      // The per-dancer participation fee is due by the Participation Fee
      // deadline — separate from, and later than, the school registration
      // deadline and the Participant Registration deadline. None of this is
      // actually owed until event registration itself is submitted.
      active: isEventRegistered && summary.participationRemaining > 0,
      dates: [invoiceSummary.participationFeeDueDate],
      label: `${participationRemainingCount} dancer(s) — participation fee outstanding`,
    },
    {
      active: isEventRegistered && summary.entryFeeRemaining > 0,
      dates: [invoiceSummary.entryFeeDueDate],
      label: `Entry fees outstanding — ${formatCurrency(summary.entryFeeRemaining)}`,
    },
    {
      // Parent consent must be completed by the Participant Registration
      // deadline — separate from the Participation Fee deadline.
      active: missingConsent.length > 0,
      dates: [invoiceSummary.participantRegistrationDueDate],
      label: `${missingConsent.length} dancer(s) missing parent consent`,
    },
    {
      active: missingMusic.length > 0,
      dates: [invoiceSummary.musicDueDate],
      label: `${missingMusic.length} ${missingMusic.length === 1 ? 'entry' : 'entries'} missing music`,
    },
  ]
    .filter(({ active, dates }) => active && isAnyDueSoonOrPastDue(...dates))
    .map(({ dates, label }) => {
      const date = earliestDate(...dates); // guaranteed non-null — isAnyDueSoonOrPastDue only passed because one of these parsed
      return { label, meta: formatDate(date), date };
    })
    .sort((a, b) => a.date - b.date);

  // Payment rows (and Music Deadline, below) turn complete from data — derived here from
  // the same already-loaded summary the outstanding banner and "View
  // invoice" buttons use (no extra Sheets reads), so they always agree.
  // Entry fees also count old-style whole-school lump payments
  // (unattributed ledger rows), which per-dancer remaining can't see —
  // same school-level total the admin payment page compares against.
  const entryFeePaidTotal = (entryFeePayments || [])
    .filter((p) => p.category === 'entry')
    .reduce((sum, p) => sum + p.amount, 0);
  const timelineStatusOverrides = {
    'Participation Fee': isEventRegistered && summary.totalDancers > 0 && summary.participationRemaining === 0 ? 'complete' : null,
    'Dance Entry Payment': !isEventRegistered || summary.entryFeeTotal === 0 ? null
      : (summary.entryFeeRemaining === 0 || entryFeePaidTotal >= summary.entryFeeTotal) ? 'complete'
        : entryFeePaidTotal > 0 ? 'in-progress' : null,
    // Complete once entries are locked (no more can be added — invoice
    // requested, deadline passed, or estimate reached; see getEffectiveLocks)
    // and every non-Freestyle entry has music. Same missingMusic list the
    // outstanding banner uses.
    'Music Deadline': locks?.entriesLocked && missingMusic.length === 0 ? 'complete' : null,
  };
  const timelineSteps = (timeline || []).filter(Boolean).map((step) => (
    timelineStatusOverrides[step.title] ? { ...step, status: timelineStatusOverrides[step.title] } : step
  ));

  const nextDeadlineInfo = nextDeadline(timelineSteps);

  return (
    <div style={{ padding: 20, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Welcome back, {school.teacherName}</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>{school.name} · {eventYear - 1}-{eventYear} season</p>

      {notRegistered && (
        <div role="alert" style={{ background: '#FCEBEB', color: '#A32D2D', border: '1px solid #EBB5B5', borderLeft: '5px solid #C62828', borderRadius: 8, padding: '14px 16px', marginBottom: 20, fontSize: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>⚠</span>
          <span style={{ flex: 1, minWidth: 220 }}>
            <strong>Your school hasn't registered for this event yet.</strong> Participants, entries and
            teachers/chaperones can't be added until you register for the event.
          </span>
          <Button size="sm" variant="primary" onClick={() => navigateTo('event-registration')}>Register for the event</Button>
        </div>
      )}

      {/* Shown once per login to a school that already added people/entries
          before registering — those were allowed before this rule existed. */}
      <AnimatePresence>
        {notRegistered && hasExistingRegistrations && !noticeDismissed && (
          <Modal onClose={closeNotice} panelStyle={{ width: 500 }}>
            <div style={{ padding: '22px 24px 18px', borderTop: '5px solid #C62828', borderRadius: 12 }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 10px', color: '#A32D2D' }}>
                Please register for the event
              </h2>
              <p style={{ fontSize: 14, color: '#2D2E33', margin: '0 0 10px', lineHeight: 1.5 }}>
                We've updated how registration works: your school now needs to <strong>register for the
                event before adding any participants, entries or teachers/chaperones</strong>.
              </p>
              <p style={{ fontSize: 14, color: '#2D2E33', margin: '0 0 18px', lineHeight: 1.5 }}>
                Your school already has registrations on file — they're safe and haven't been removed. Once
                you've registered for the event, you'll be able to carry on adding and editing as usual.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button size="sm" onClick={closeNotice}>Later</Button>
                <Button size="sm" variant="primary" onClick={() => { closeNotice(); navigateTo('event-registration'); }}>
                  Register for the event
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {locks?.waitlisted && (
        <div style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: 8, padding: '14px 16px', marginBottom: 20, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>⏳</span>
          <span>
            <strong>Your school is on the event waitlist.</strong> Your registration was received, but the event
            was at capacity when you submitted — you can't add participants or entries yet. OSSDF will let you
            know if space becomes available.
          </span>
        </div>
      )}

      {isWithdrawn && (
        <div style={{ background: '#F0F0F0', color: '#666666', borderRadius: 8, padding: '14px 16px', marginBottom: 20, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>⊘</span>
          <span>
            <strong>Your school has withdrawn from this event.</strong> Your registration fee has already been
            processed and is non-refundable. You can no longer add participants or entries for this event.
          </span>
        </div>
      )}

      {outstandingItems.length > 0 && (
        <OutstandingBanner
          id="dashboard-outstanding"
          title="Items need your attention"
          items={outstandingItems}
        />
      )}

      <Card
        title={currEvent ? currEvent.eventName : 'Event Details'}
        subtitle={currEvent ? `${currEvent.location} · ${eventDateRange}` : ''}
        action={currEvent?.infoDocUrl ? (
          <Button size="sm" onClick={() => window.open(currEvent.infoDocUrl, '_blank', 'noopener,noreferrer')}>
            More details ↗
          </Button>
        ) : null}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <StatCard label="Dancers registered" value={dancers ? dancers.length : '—'} />
        <StatCard label="Dance entries" value={entries?.length ?? '—'} />
        <StatCard label="Next deadline" value={nextDeadlineInfo.date} sub={nextDeadlineInfo.title} small />
      </div>

      <Card
        title={`Timeline`}
        //subtitle={currEvent ? `${currEvent.location} · ${eventDateRange}` : ''}
      >
        {timelineSteps.map((step, i, steps) => {
          const feeDue = feeInvoiceActions[step.title] && step.status !== 'complete';
          // Green only for whichever unpaid fee is the single nearest due
          // date (matching the "Next deadline" stat card above) — every
          // other still-outstanding fee keeps its button, just not
          // visually competing for attention with the one due next.
          const rowStep = feeDue
            ? { ...step, actionLabel: 'View invoice', actionTarget: 'invoices', actionVariant: step.title === nextDeadlineInfo.title ? 'primary' : 'default' }
            : step;
          return <TimelineRow key={step.id} step={rowStep} prices={feeConfig.ENTRY_RATES} isLast={i === steps.length - 1} navigateTo={navigateTo} />;
        })}
      </Card>
    </div>
  );
}

function StatCard({ label, value, sub, small }) {
  return (
    <div style={{ background: '#F5F5F5', borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: small ? 16 : 22, fontWeight: 500 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Timeline dates are free-typed text from the Information sheet (e.g.
// "1/31/2027"), not always a strict date — anything unparseable is treated
// as "no badge"/excluded rather than always shown. One row is the exception:
// the auto-generated "Event" row (see syncEventDatesToTimeline on the
// backend) holds a range like "October 30 – October 31, 2026", which
// `new Date(...)` can't parse directly since it names two dates — the year
// only appears once, attached to the end date. Reconstruct the *start* date
// from a range so that row still counts as "upcoming" instead of silently
// dropping out and leaving the Next Deadline card blank.
function parseTimelineDate(dateStr) {
  if (!dateStr) return null;
  const direct = new Date(dateStr);
  if (!isNaN(direct)) return direct;
  const year = String(dateStr).match(/\d{4}/)?.[0];
  const firstPart = String(dateStr).split('–')[0].trim();
  if (!year || !firstPart) return null;
  const rangeStart = new Date(`${firstPart}, ${year}`);
  return isNaN(rangeStart) ? null : rangeStart;
}

// Only applies to 'upcoming' rows; tasks with real status tracking
// (complete/in-progress/etc.) always show their badge regardless of date.
function upcomingBadge(dateStr) {
  const target = parseTimelineDate(dateStr);
  if (!target) return null;
  const now = new Date();
  if (target < now) return { label: 'Past Due', variant: 'danger' };
  const oneMonthOut = new Date(now);
  oneMonthOut.setMonth(oneMonthOut.getMonth() + 1);
  if (target <= oneMonthOut) return { label: 'Upcoming', variant: 'neutral' };
  return null;
}

// Earliest not-yet-complete timeline date that hasn't already passed, for
// the "Next deadline" stat card — both the date and which task it's for, so
// the card doesn't just say "Aug 25" with no indication of what's due then.
function nextDeadline(timeline) {
  const now = new Date();
  const upcoming = (timeline || [])
    .filter((step) => step.status !== 'complete')
    .map((step) => ({ title: step.title, date: parseTimelineDate(step.date) }))
    .filter(({ date }) => date && date >= now)
    .sort((a, b) => a.date - b.date);
  if (!upcoming.length) return { date: '—', title: '' };
  return { date: upcoming[0].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), title: upcoming[0].title };
}

// Timeline dates are usually a single date ("October 31, 2026") — prefixing
// the weekday makes it easier to see at a glance exactly which day of the
// week something's due, without having to check a calendar. The one
// exception is the auto-generated "Event" row, which holds a range ("May 25
// – May 28, 2027") rather than a single date — a single weekday doesn't
// meaningfully describe a multi-day range, so that row's text is left as-is.
function formatTimelineDate(dateStr) {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function TimelineRow({ step, prices, isLast, navigateTo }) {
  const dot = DOT_STYLE[step.status] || DOT_STYLE.upcoming;
  const upcoming = step.status === 'upcoming' ? upcomingBadge(step.date) : null;
  const badgeLabel = step.status === 'upcoming' ? upcoming?.label : STATUS_LABEL[step.status];
  const badgeVariant = step.status === 'upcoming' ? upcoming?.variant : STATUS_VARIANT[step.status];
  const showBadge = step.status !== 'upcoming' || !!upcoming;
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28, flexShrink: 0 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: dot.background,
            color: dot.color,
            fontSize: 13,
          }}
        >
          {dot.icon}
        </div>
      </div>
      <div style={{ flex: 1, borderLeft: isLast ? 'none' : '1px solid #E5E5E5', marginLeft: -14, paddingLeft: 30, marginTop: -28, paddingTop: 28, paddingBottom: isLast ? 0 : 18 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>
          {step.title} {showBadge && <Badge variant={badgeVariant}>{badgeLabel}</Badge>}
        </div>
        {step.information && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{resolveTimelineInfo(step.information, prices)}</div>}
        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{formatTimelineDate(step.date)}</div>
        {step.actionLabel && (
          <div style={{ marginTop: 8 }}>
            <Button size="sm" variant={step.actionVariant || ((step.status === 'in-progress' || step.status === 'not-registered') ? 'primary' : 'default')} onClick={() => navigateTo(step.actionTarget)}>
              → {step.actionLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
